import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Upload, Star, Pencil, Trash2, ArrowLeft } from 'lucide-react';
import { api, ApiClientError } from '../../../lib/api.js';
import { Button } from '../../../components/ui/Button.jsx';
import { TextField, SelectField } from '../../../components/ui/Field.jsx';

const DOC_TYPE_LABEL = {
  invoice: 'Sales invoice',
  gst_invoice: 'GST tax invoice',
  gold_purchase: 'Gold purchase',
  repair: 'Repair voucher',
  issue: 'Issue voucher',
  receipt: 'Receipt voucher',
  chit_receipt: 'Chit receipt',
  report: 'Report',
};

const DOC_TYPES = Object.keys(DOC_TYPE_LABEL);
const PAPERS = ['a4', 'thermal58', 'thermal80'];

function UploadForm({ onUploaded }) {
  const [name, setName] = useState('');
  const [docType, setDocType] = useState('invoice');
  const [paper, setPaper] = useState('a4');
  const [file, setFile] = useState(null);
  const [error, setError] = useState(null);

  const uploadMutation = useMutation({
    mutationFn: () => {
      const form = new FormData();
      form.append('name', name);
      form.append('docType', docType);
      form.append('paper', paper);
      form.append('file', file);
      return api.post('/pdf-templates', form);
    },
    onSuccess: (template) => {
      setName('');
      setFile(null);
      onUploaded(template);
    },
    onError: (err) => setError(err instanceof ApiClientError ? err.message : 'Could not upload this template.'),
  });

  const canSubmit = name.trim().length > 0 && !!file;

  return (
    <form
      onSubmit={(e) => { e.preventDefault(); setError(null); uploadMutation.mutate(); }}
      className="card flex flex-col gap-3 p-4"
    >
      <h2 className="text-xs font-semibold uppercase tracking-wide text-muted">Upload a Canva template</h2>
      <p className="text-sm text-muted">
        Design the voucher in Canva leaving the blanks empty, then export as <b>PDF Print</b> and upload it here.
      </p>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <TextField label="Name" required value={name} onChange={(e) => setName(e.target.value)} />
        <SelectField label="Document type" value={docType} onChange={(e) => setDocType(e.target.value)}>
          {DOC_TYPES.map((t) => (
            <option key={t} value={t}>{DOC_TYPE_LABEL[t]}</option>
          ))}
        </SelectField>
        <SelectField label="Paper" value={paper} onChange={(e) => setPaper(e.target.value)}>
          {PAPERS.map((p) => (
            <option key={p} value={p}>{p}</option>
          ))}
        </SelectField>
      </div>
      <input
        type="file"
        accept="application/pdf"
        onChange={(e) => setFile(e.target.files?.[0] ?? null)}
        className="text-sm"
      />
      {error ? <p className="text-sm text-danger">{error}</p> : null}
      <Button type="submit" disabled={!canSubmit} loading={uploadMutation.isPending} className="self-start">
        <Upload size={16} aria-hidden="true" /> Upload
      </Button>
    </form>
  );
}

function TemplateRow({ template, onChanged }) {
  const queryClient = useQueryClient();

  const setDefaultMutation = useMutation({
    mutationFn: () => api.patch(`/pdf-templates/${template.id}`, { isDefault: true }),
    onSuccess: onChanged,
  });
  const toggleActiveMutation = useMutation({
    mutationFn: () => api.patch(`/pdf-templates/${template.id}`, { isActive: !template.is_active }),
    onSuccess: onChanged,
  });
  const deleteMutation = useMutation({
    mutationFn: () => api.delete(`/pdf-templates/${template.id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['pdf-templates'] }),
  });

  return (
    <div className="flex items-center gap-3 border-b border-border py-3 last:border-0">
      <div className="min-w-0 flex-1">
        <p className="truncate font-medium">{template.name}</p>
        <p className="text-xs text-muted">
          {DOC_TYPE_LABEL[template.doc_type] ?? template.doc_type} · {template.paper} · {template.page_count} page(s)
          {template.is_default ? <span className="ml-2 text-primary">Default</span> : null}
          {!template.is_active ? <span className="ml-2 text-danger">Inactive</span> : null}
        </p>
      </div>
      <Link to={`/settings/templates/${template.id}`} className="flex h-9 w-9 items-center justify-center rounded-card text-muted hover:bg-surface-2 hover:text-content" aria-label="Map fields">
        <Pencil size={16} aria-hidden="true" />
      </Link>
      {!template.is_default ? (
        <button
          type="button"
          onClick={() => setDefaultMutation.mutate()}
          disabled={setDefaultMutation.isPending}
          className="flex h-9 w-9 items-center justify-center rounded-card text-muted hover:bg-surface-2 hover:text-content"
          aria-label="Make default"
          title="Make default"
        >
          <Star size={16} aria-hidden="true" />
        </button>
      ) : null}
      <button
        type="button"
        onClick={() => toggleActiveMutation.mutate()}
        disabled={toggleActiveMutation.isPending}
        className="text-xs text-muted hover:underline"
      >
        {template.is_active ? 'Deactivate' : 'Activate'}
      </button>
      <button
        type="button"
        onClick={() => deleteMutation.mutate()}
        disabled={deleteMutation.isPending}
        className="flex h-9 w-9 items-center justify-center rounded-card text-muted hover:bg-danger/10 hover:text-danger"
        aria-label="Delete"
      >
        <Trash2 size={16} aria-hidden="true" />
      </button>
    </div>
  );
}

export default function TemplatesListPage() {
  const queryClient = useQueryClient();
  const { data: templates, isLoading } = useQuery({
    queryKey: ['pdf-templates'],
    queryFn: () => api.get('/pdf-templates'),
  });

  const refetch = () => queryClient.invalidateQueries({ queryKey: ['pdf-templates'] });

  const grouped = (templates ?? []).reduce((acc, t) => {
    (acc[t.doc_type] ??= []).push(t);
    return acc;
  }, {});

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-center gap-3">
        <Link to="/settings" className="flex h-touch w-touch items-center justify-center rounded-card hover:bg-surface-2">
          <ArrowLeft size={20} aria-hidden="true" />
        </Link>
        <h1 className="font-display text-2xl font-semibold tracking-tight">Document templates</h1>
      </div>
      <p className="-mt-3 max-w-2xl text-sm text-muted">
        The built-in layout is used unless a Canva template here is marked both <b>default</b> and <b>active</b> for
        that document type and paper size.
      </p>

      <UploadForm onUploaded={refetch} />

      {isLoading ? null : !templates?.length ? (
        <p className="text-sm text-muted">No templates uploaded yet.</p>
      ) : (
        Object.entries(grouped).map(([docType, rows]) => (
          <section key={docType} className="card p-4">
            <h2 className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted">
              {DOC_TYPE_LABEL[docType] ?? docType}
            </h2>
            {rows.map((t) => (
              <TemplateRow key={t.id} template={t} onChanged={refetch} />
            ))}
          </section>
        ))
      )}
    </div>
  );
}
